import { useState, useEffect, useCallback } from 'react';
import { Spinner, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { getIncidentReportById, downloadIncidentPDF, getIncidentSignedUrls } from './FormikHelpers/Api';
import { Previewer } from '../../../../../components/DocumentPreviewer/Previewer';

const PERSON_TYPE_LABELS = { employee: 'Employee', tenant: 'Tenant', contractor: 'Contractor', other: 'Other' };

const formatLabel = (value) => {
  if (!value) return null;
  return value.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const fmt = (iso, opts = {}) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      ...opts,
    }).format(d);
  } catch { return '—'; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  } catch { return '—'; }
};

const PERMITTED_ROLES = ['admin', 'company', 'manager', 'technical staff'];
const display = (v) => (v === null || v === undefined || v === '') ? null : String(v);
const fileNameFromKey = (k) => (typeof k === 'string' ? k.split('/').pop() : '');
const getFileExt = (k) => (typeof k === 'string' ? k.split('.').pop()?.toLowerCase() : '');
const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
const isPdfKey = (k) => /\.pdf$/i.test(k);
const isImageKey = (k) => /\.(jpe?g|png|gif|webp)$/i.test(k);

const statusCfg = {
  Open: { bg: '#fef3c7', color: '#92400e', border: '#fde68a', icon: 'alert-triangle' },
  InProgress: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe', icon: 'activity' },
  Pending: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', icon: 'clock' },
  Resolved: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', icon: 'check-circle' },
};

const PreviewField = ({ label, value, wide, full }) => {
  const isEmpty = !value || value === '—';
  return (
    <div style={{
      gridColumn: full ? '1 / -1' : wide ? 'span 2' : 'span 1',
      background: '#fff',
      border: '1px solid #dbe6f0',
      borderRadius: 10,
      padding: '12px 16px',
      minHeight: 56,
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6a8ca8', marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: isEmpty ? 400 : 500, color: isEmpty ? '#a6c4da' : '#0F172A', lineHeight: 1.5, whiteSpace: full ? 'pre-wrap' : 'normal' }}>
        {isEmpty ? '—' : value}
      </div>
    </div>
  );
};

const PreviewSection = ({ icon, title, children, accentColor }) => {
  const color = accentColor || '#163b5f';
  const gradEnd = accentColor ? accentColor : '#3a6b98';
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: `2px solid ${accentColor ? accentColor + '33' : '#dbe6f0'}`, marginBottom: 14 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg,${color},${gradEnd})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 4px 10px ${color}40` }}>
          <FeatherIcon icon={icon} size={14} color="#fff" />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {children}
      </div>
    </div>
  );
};

const SectionBlock = ({ icon, title, accentColor, children }) => {
  const color = accentColor || '#163b5f';
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: `1.5px solid ${color}33`, marginBottom: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(22,59,95,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 22px', background: `linear-gradient(135deg, #0b1d31 0%, ${color} 70%, #eef3f7 100%)`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -30, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.3)', flexShrink: 0 }}>
          <FeatherIcon icon={icon} size={18} color="#fff" />
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{title}</div>
        </div>
      </div>
      <div style={{ padding: '20px 22px' }}>{children}</div>
    </div>
  );
};

const AttachmentThumbnail = ({ fileKey, signedUrl, onClick }) => {
  const isImage = isImageKey(fileKey);
  const isVideo = isVideoKey(fileKey);
  return (
    <div
      onClick={onClick}
      style={{ width: 88, height: 88, borderRadius: 10, border: '2px solid #c2d5e3', overflow: 'hidden', cursor: 'pointer', background: '#eef3f7', position: 'relative', flexShrink: 0, transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(22,59,95,0.1)' }}
    >
      {isImage ? (
        signedUrl ? (
          <img src={signedUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spinner size="sm" color="secondary" />
          </div>
        )
      ) : isVideo ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <FeatherIcon icon="video" size={26} color="#163b5f" />
        </div>
      ) : isPdfKey(fileKey) ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <FeatherIcon icon="file-text" size={26} color="#163b5f" />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <FeatherIcon icon="file" size={26} color="#163b5f" />
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(22,59,95,0.65)', padding: '2px 4px', fontSize: 8.5, color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {fileNameFromKey(fileKey)}
      </div>
    </div>
  );
};

const FilePreviewModal = ({ isOpen, toggle, fileKey, signedUrl, fileName: name, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'Escape') toggle();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onPrev, onNext, toggle]);

  if (!isOpen || !signedUrl) return null;
  const ext = getFileExt(fileKey);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = signedUrl;
    a.download = name || fileNameFromKey(fileKey);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static" style={{ zIndex: 9999 }}>
      <ModalHeader toggle={toggle} style={{ background: 'linear-gradient(135deg, #0b1d31 0%, #163b5f 60%, #eef3f7 100%)', border: 'none', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <span style={{ color: '#fff', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.2)', fontSize: 14 }}>
            {isVideoKey(fileKey) ? '🎬' : isPdfKey(fileKey) ? '📄' : isImageKey(fileKey) ? '🖼' : '📁'} {name || fileNameFromKey(fileKey)}
          </span>
          <button onClick={handleDownload} style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
            <FeatherIcon icon="download" size={13} /> Download
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="p-0" style={{ background: '#F8FAFC', position: 'relative', minHeight: 300 }}>
        {onPrev && (
          <button onClick={onPrev} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <FeatherIcon icon="chevron-left" size={20} />
          </button>
        )}
        {onNext && (
          <button onClick={onNext} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <FeatherIcon icon="chevron-right" size={20} />
          </button>
        )}
        <div style={{ padding: '16px 56px' }}>
          <Previewer signedUrl={signedUrl} type={ext} url={fileKey} height="70vh" width="100%" />
        </div>
      </ModalBody>
    </Modal>
  );
};

const AttachmentsSection = ({ attachments, signedUrlCache, onPreview }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #dbe6f0', marginBottom: 14 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(22,59,95,0.3)' }}>
          <FeatherIcon icon="paperclip" size={14} color="#fff" />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: '#163b5f', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Attachments ({attachments.length})
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {attachments.map((key, ai) => (
          <AttachmentThumbnail key={ai} fileKey={key} signedUrl={signedUrlCache[key]} onClick={() => onPreview(key, attachments, ai)} />
        ))}
      </div>
    </div>
  );
};

const SingleDocField = ({ label, fileKey, signedUrlCache, onPreview }) => {
  if (!fileKey || fileKey === 'N/A') {
    return <PreviewField label={label} value={fileKey === 'N/A' ? 'N/A' : null} />;
  }
  return (
    <div style={{ gridColumn: 'span 1', background: '#fff', border: '1px solid #dbe6f0', borderRadius: 10, padding: '12px 16px', minHeight: 56 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6a8ca8', marginBottom: 8 }}>{label}</div>
      <div
        onClick={() => onPreview(fileKey, [fileKey], 0)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: 'rgba(22,59,95,0.05)', border: '1px solid rgba(22,59,95,0.15)', transition: 'all 0.15s' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(22,59,95,0.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(22,59,95,0.05)'; }}
      >
        <FeatherIcon icon={isPdfKey(fileKey) ? 'file-text' : isVideoKey(fileKey) ? 'video' : isImageKey(fileKey) ? 'image' : 'file'} size={14} color="#163b5f" />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#163b5f', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileNameFromKey(fileKey)}</span>
        <FeatherIcon icon="eye" size={13} color="#3a6b98" />
      </div>
    </div>
  );
};

const IncidentReportPreview = ({ isOpen, onClose, onCancel, reportId, reportData, userRole, onSubmit }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(!reportData && !!reportId);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [signedUrlCache, setSignedUrlCache] = useState({});
  const [previewState, setPreviewState] = useState(null);

  const canViewInvestigation = PERMITTED_ROLES.includes(userRole) || reportData;

  const fetchReport = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getIncidentReportById(reportId);
      setReport(data);
    } catch {
      setError('Failed to load incident report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    if (isOpen) {
      if (reportData) {
        setReport(reportData);
        setLoading(false);
        requestAnimationFrame(() => setVisible(true));
      } else if (reportId) {
        fetchReport();
        requestAnimationFrame(() => setVisible(true));
      }
    } else {
      setVisible(false);
      setReport(null);
      setSignedUrlCache({});
      setPreviewState(null);
    }
  }, [isOpen, fetchReport, reportId, reportData]);

  useEffect(() => {
    if (!report) return;
    const allKeys = [];
    (report.notification?.personsInvolved || []).forEach((p) => {
      (p?.attachments || []).forEach((k) => allKeys.push(k));
    });
    const inv = report.investigation || {};
    (inv.attachments || []).forEach((k) => allKeys.push(k));
    if (inv.leaseDocument && inv.leaseDocument !== 'N/A') allKeys.push(inv.leaseDocument);
    if (inv.carInsuranceDocument && inv.carInsuranceDocument !== 'N/A') allKeys.push(inv.carInsuranceDocument);
    if (inv.homeInsuranceDocument && inv.homeInsuranceDocument !== 'N/A') allKeys.push(inv.homeInsuranceDocument);
    const missing = allKeys.filter((k) => k && !signedUrlCache[k]);
    if (missing.length) {
      getIncidentSignedUrls(missing)
        .then((urls) => setSignedUrlCache((prev) => ({ ...prev, ...urls })))
        .catch(() => { });
    }
  }, [report]);

  const handleDownloadPDF = async () => {
    if (!reportId) return;
    setPdfLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      await downloadIncidentPDF(reportId, tz);
    } finally {
      setPdfLoading(false);
    }
  };

  const openPreview = useCallback(async (key, list, index) => {
    if (signedUrlCache[key]) {
      setPreviewState({ fileKey: key, signedUrl: signedUrlCache[key], list, index });
      return;
    }
    try {
      const urls = await getIncidentSignedUrls([key]);
      if (urls[key]) {
        setSignedUrlCache((prev) => ({ ...prev, ...urls }));
        setPreviewState({ fileKey: key, signedUrl: urls[key], list, index });
      }
    } catch {
      toast.error('Failed to load preview');
    }
  }, [signedUrlCache]);

  const navigatePreview = (delta) => {
    if (!previewState?.list) return;
    const newIdx = previewState.index + delta;
    if (newIdx >= 0 && newIdx < previewState.list.length) {
      openPreview(previewState.list[newIdx], previewState.list, newIdx);
    }
  };

  if (!isOpen) return null;

  const status = report?.status || 'Open';
  const sCfg = statusCfg[status] || statusCfg.Open;
  const referenceId = report?.referenceId
    || (report?.sequenceNumber ? `#${String(report.sequenceNumber).padStart(4, '0')}` : null);

  const handleClose = onCancel || onClose;

  return (
    <>
      {/* Backdrop — less transparent, does NOT close modal */}
      <div
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(6, 21, 36, 0.72)',
          zIndex: 1200,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Centered modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: visible ? 'translate(-50%, -50%)' : 'translate(-50%, calc(-50% + 24px))',
          width: '92vw',
          maxWidth: 860,
          maxHeight: '92vh',
          background: '#eef3f7',
          boxShadow: '0 32px 80px rgba(6,21,36,0.35), 0 0 0 1px rgba(22,59,95,0.12)',
          zIndex: 1201,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 20,
          overflow: 'hidden',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.25s ease',
        }}
      >
        {/* Header */}
        <div style={{
          flexShrink: 0,
          background: 'linear-gradient(135deg, #0b1d31 0%, #163b5f 60%, #eef3f7 100%)',
          padding: '20px 24px',
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>
          <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FeatherIcon icon="file-text" size={20} color="#fff" />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                {onSubmit ? 'Preview Incident Report' : 'Incident Report'}
              </h2>
              {referenceId && (
                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 7, padding: '2px 9px', color: '#fff' }}>
                  {referenceId}
                </span>
              )}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: sCfg.bg, color: sCfg.color, border: `1px solid ${sCfg.border}`, borderRadius: 20, padding: '3px 11px', fontSize: 11.5, fontWeight: 700 }}>
                <FeatherIcon icon={sCfg.icon} size={11} />
                {status}
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
              {report ? `Created ${fmt(report.createdAt, { hour: undefined, minute: undefined })}` : 'Loading…'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {report && reportId && !onSubmit && (
              <Button color="success" size="sm" onClick={handleDownloadPDF} disabled={pdfLoading}
                style={{ borderRadius: 9, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, background: 'rgba(255,255,255,0.95)', color: '#0b1d31', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                {pdfLoading ? <><Spinner size="sm" /> Generating…</> : <><FeatherIcon icon="download" size={14} /> PDF</>}
              </Button>
            )}
            <button
              onClick={handleClose}
              style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
            >
              <FeatherIcon icon="x" size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px 8px' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 14 }}>
              <Spinner color="primary" style={{ width: 40, height: 40 }} />
              <span style={{ color: '#64748b', fontSize: 14 }}>Loading incident report…</span>
            </div>
          )}

          {error && !loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '50px 24px', gap: 10 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FeatherIcon icon="alert-circle" size={22} color="#ef4444" />
              </div>
              <p style={{ color: '#ef4444', fontWeight: 600, margin: 0 }}>{error}</p>
              <Button color="primary" outline size="sm" onClick={fetchReport}>Try Again</Button>
            </div>
          )}

          {!loading && !error && report && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {/* 1. Manager Information */}
              <SectionBlock icon="briefcase" title="Manager Information" accentColor="#163b5f">
                <PreviewSection icon="info" title="General Information">
                  <PreviewField label="Manager Reporting" value={report.notification?.managerReporting} />
                  <PreviewField label="Manager Email" value={report.notification?.managerEmail} />
                  <PreviewField label="Manager Phone" value={report.notification?.managerPhoneNo} />
                  <PreviewField label="Property Building" value={report.propertyDetails?.buildingName} />
                  <PreviewField label="Report Status" value={status} />
                  <PreviewField label="Reference ID" value={report.referenceId} />
                  <PreviewField label="Follow-up Required" value={report.followUpRequired ? 'Yes' : 'No'} />
                  {report.comments && <PreviewField label="Comments" value={report.comments} full />}
                </PreviewSection>
              </SectionBlock>

              {/* 2. Resident Information — one card per person */}
              {(report.notification?.personsInvolved || []).map((p, i) => {
                const typeDisplay = p.type
                  ? (p.type === 'other' && p.specifyType ? `Other (${p.specifyType})` : PERSON_TYPE_LABELS[p.type] || formatLabel(p.type))
                  : null;
                return (
                  <SectionBlock
                    key={i}
                    icon="user"
                    title={`Resident Information${(report.notification?.personsInvolved || []).length > 1 ? ` — ${[p.firstName, p.lastName].filter(Boolean).join(' ') || `Person ${i + 1}`}` : ''}`}
                    accentColor="#3a6b98"
                  >
                    <PreviewSection icon="user" title="Personal Details">
                      <PreviewField label="First Name" value={display(p.firstName)} />
                      <PreviewField label="Middle Name" value={display(p.middleName)} />
                      <PreviewField label="Last Name" value={display(p.lastName)} />
                      <PreviewField label="Person Type" value={typeDisplay} />
                      <PreviewField label="Email" value={display(p.email)} wide />
                      <PreviewField label="Phone Number(s)" value={display((Array.isArray(p.phoneNumbers) && p.phoneNumbers.filter(Boolean).length > 0) ? p.phoneNumbers.filter(Boolean).join(', ') : p.phoneNo)} />
                      <PreviewField label="Unit No." value={display(p.unitNo)} />
                      <PreviewField label="Home Address" value={display(p.homeAddress || p.address)} wide />
                      <PreviewField label="Notification to Property Manager" value={fmt(p.notificationToPropertyManagerDateTime)} wide />
                      <PreviewField label="Statement" value={display(p.statement)} full />
                    </PreviewSection>

                    <AttachmentsSection attachments={p.attachments} signedUrlCache={signedUrlCache} onPreview={openPreview} />

                    {p.witnesses && p.witnesses.length > 0 && (
                      <PreviewSection icon="users" title="Witnesses">
                        <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {p.witnesses.map((w, wi) => (
                            <div key={wi} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>
                                {[w.firstName, w.lastName].filter(Boolean).join(' ') || `Witness ${wi + 1}`}
                              </div>
                              <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                                {w.email && <span>✉ {w.email}</span>}
                                {w.phoneNo && <span>📞 {w.phoneNo}</span>}
                                {w.address && <span>📍 {w.address}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </PreviewSection>
                    )}

                    {p.signature?.signature && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #d1fae5', marginBottom: 14 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(5,150,105,0.3)' }}>
                            <FeatherIcon icon="edit-3" size={14} color="#fff" />
                          </div>
                          <span style={{ fontSize: 11.5, fontWeight: 800, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Signature</span>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 18 }}>
                          <div style={{ background: '#fff', border: '1px solid #a7f3d0', borderRadius: 8, padding: 8 }}>
                            <img src={p.signature.signature} alt="Signature" style={{ height: 52, maxWidth: 220, objectFit: 'contain' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13, color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FeatherIcon icon="check-circle" size={15} /> Signed & Verified
                            </div>
                            {p.signature.date && <div style={{ fontSize: 12, color: '#047857', marginTop: 3, fontWeight: 500 }}>{fmtDate(p.signature.date)}</div>}
                          </div>
                        </div>
                      </div>
                    )}
                  </SectionBlock>
                );
              })}

              {/* 3. Investigation Information */}
              {canViewInvestigation ? (
                <SectionBlock icon="search" title="Investigation Information" accentColor="#059669">
                  {(() => {
                    const inv = report.investigation || {};
                    return (
                      <>
                        <PreviewSection icon="info" title="Investigation Details" accentColor="#059669">
                          <PreviewField label="Occurrence Date/Time" value={fmt(inv.occurrenceDateTime)} />
                          <PreviewField label="Manager Notified" value={fmt(inv.notificationToPropertyManagerDateTime)} />
                          <PreviewField label="Temperature (°C)" value={inv.temperatureCelsius != null ? `${inv.temperatureCelsius}°C` : null} />
                          <PreviewField label="Weather Conditions" value={(inv.weatherConditions || []).join(', ') || null} />
                          <PreviewField label="Environment Conditions" value={(inv.environmentConditions || []).join(', ') || null} />
                          <PreviewField label="Visibility" value={(inv.visibility || []).join(', ') || null} />
                          <PreviewField label="Investigation Statement" value={display(inv.statement)} full />
                        </PreviewSection>

                        {(inv.leaseDocument || inv.carInsuranceDocument || inv.homeInsuranceDocument) && (
                          <PreviewSection icon="folder" title="Supporting Documents" accentColor="#059669">
                            <SingleDocField label="Lease Document" fileKey={inv.leaseDocument} signedUrlCache={signedUrlCache} onPreview={openPreview} />
                            <SingleDocField label="Car Insurance" fileKey={inv.carInsuranceDocument} signedUrlCache={signedUrlCache} onPreview={openPreview} />
                            <SingleDocField label="Home Insurance" fileKey={inv.homeInsuranceDocument} signedUrlCache={signedUrlCache} onPreview={openPreview} />
                          </PreviewSection>
                        )}

                        <AttachmentsSection attachments={inv.attachments} signedUrlCache={signedUrlCache} onPreview={openPreview} />

                        {inv.witnesses && inv.witnesses.length > 0 && (
                          <PreviewSection icon="users" title="Witnesses" accentColor="#059669">
                            <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {inv.witnesses.map((w, i) => (
                                <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                                  <div style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>
                                    {[w.firstName, w.lastName].filter(Boolean).join(' ') || `Witness ${i + 1}`}
                                  </div>
                                  <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                                    {w.email && <span>✉ {w.email}</span>}
                                    {w.phoneNo && <span>📞 {w.phoneNo}</span>}
                                    {w.address && <span>📍 {w.address}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </PreviewSection>
                        )}

                        {inv.investigationDoneBy?.name && (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px', borderBottom: '2px solid #d1fae5', marginBottom: 14 }}>
                              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(5,150,105,0.3)' }}>
                                <FeatherIcon icon="check-circle" size={14} color="#fff" />
                              </div>
                              <span style={{ fontSize: 11.5, fontWeight: 800, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Investigation Completed By</span>
                            </div>
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div>
                                <div style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>{inv.investigationDoneBy.name}</div>
                                {inv.investigationDoneBy.date && (
                                  <div style={{ fontSize: 12, color: '#4ade80' }}>{fmtDate(inv.investigationDoneBy.date)}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </SectionBlock>
              ) : (
                <div style={{ padding: 20, background: '#f8fafc', border: '1.5px dashed #c2d5e3', borderRadius: 14, textAlign: 'center' }}>
                  <FeatherIcon icon="lock" size={22} color="#94a3b8" />
                  <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                    Investigation details are only visible to managers and authorized staff.
                  </p>
                </div>
              )}

              {/* Notification recipients (no section header — supplemental) */}
              {report.notificationEmails?.length > 0 && (
                <SectionBlock icon="send" title="Notification Recipients" accentColor="#6366f1">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {report.notificationEmails.map((n, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px', background: '#eef3f7', border: '1px solid #c2d5e3', borderRadius: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FeatherIcon icon="mail" size={13} color="#163b5f" />
                          <span style={{ fontSize: 13, color: '#163b5f', fontWeight: 600 }}>
                            {[n.firstName, n.lastName].filter(Boolean).join(' ')}
                            {n.email ? ` <${n.email}>` : ''}
                          </span>
                        </div>
                        {n.managersNote && (
                          <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic', paddingLeft: 23, borderLeft: '2px solid #cbd5e1', marginLeft: 6 }}>
                            Note: "{n.managersNote}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </SectionBlock>
              )}

              {/* Status history */}
              {report.statusHistory?.length > 0 && (
                <SectionBlock icon="clock" title="Status History" accentColor="#7c3aed">
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {[...report.statusHistory].reverse().map((h, i) => {
                      const hCfg = statusCfg[h.status] || {};
                      return (
                        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0', borderBottom: i < report.statusHistory.length - 1 ? '1px solid #eef3f7' : 'none' }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: hCfg.bg || '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FeatherIcon icon={hCfg.icon || 'circle'} size={13} color={hCfg.color || '#64748b'} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{h.status}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {fmt(h.timestamp || h.changedAt)} {h.changedBy?.role ? `· ${h.changedBy.role}` : ''}
                            </div>
                            {h.comments && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{h.comments}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SectionBlock>
              )}

              {/* Spacer so last card isn't right against sticky footer */}
              <div style={{ height: 8 }} />
            </div>
          )}
        </div>

        {/* Sticky footer with action buttons */}
        <div style={{
          flexShrink: 0,
          background: '#fff',
          borderTop: '1.5px solid rgba(22,59,95,0.12)',
          padding: '14px 22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: onSubmit ? 'space-between' : 'flex-end',
          gap: 10,
          boxShadow: '0 -4px 16px rgba(6,21,36,0.06)',
        }}>
          {onSubmit && (
            <p style={{ margin: 0, fontSize: 12, color: '#6a8ca8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FeatherIcon icon="info" size={13} color="#6a8ca8" />
              Review all sections before confirming submission.
            </p>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              onClick={handleClose}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 20px',
                background: 'transparent', color: '#3a6b98',
                border: '1.5px solid rgba(22,59,95,0.25)',
                borderRadius: 10, fontSize: 13.5, fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
                transition: 'all 0.15s',
              }}
            >
              <FeatherIcon icon="arrow-left" size={14} />
              {onSubmit ? 'Back & Edit' : 'Close'}
            </button>
            {onSubmit && (
              <button
                onClick={onSubmit}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 26px',
                  background: 'linear-gradient(135deg,#059669,#10b981)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
                  boxShadow: '0 4px 18px rgba(5,150,105,0.4)',
                  transition: 'all 0.2s',
                }}
              >
                <FeatherIcon icon="check" size={15} />
                Confirm & Submit
              </button>
            )}
          </div>
        </div>
      </div>

      <FilePreviewModal
        isOpen={!!previewState}
        toggle={() => setPreviewState(null)}
        fileKey={previewState?.fileKey}
        signedUrl={previewState?.signedUrl}
        fileName={previewState?.fileKey ? fileNameFromKey(previewState.fileKey) : ''}
        onPrev={previewState && previewState.index > 0 ? () => navigatePreview(-1) : null}
        onNext={previewState && previewState.list && previewState.index < previewState.list.length - 1 ? () => navigatePreview(1) : null}
      />
    </>
  );
};

export { IncidentReportPreview };